import React from 'react'

import { Button, MetaPill, Text } from '@doist/marketist'

import { useTranslation } from 'next-i18next'
import { Integration } from 'types/contentful'

import css from './IntegrationAppAsideInfo.module.css'

type IntegrationAppAsideInfoProps = {
    integration: Integration
}

export function IntegrationAppAsideInfo({ integration }: IntegrationAppAsideInfoProps) {
    const { t } = useTranslation('integrations')
    const { commonContent } = integration
    const { appWebsiteLink, privacyPolicyLink, supportLink, supportedLanguages, learnMoreButton } =
        commonContent
    const categoryName = commonContent?.category?.name
    const moreLinks = [
        {
            text: integration.zapier ? t('asideInfo.zapierWebsite') : t('asideInfo.developer'),
            href: appWebsiteLink,
            icon: 'Globe',
        },
        { text: t('asideInfo.support'), href: supportLink, icon: 'Mail' },
        { text: t('asideInfo.help'), href: learnMoreButton, icon: 'Help' },
        { text: t('asideInfo.privacy'), href: privacyPolicyLink, icon: 'Lock' },
    ]

    return (
        <aside className={css.asideInfo}>
            {categoryName ? (
                <Section heading={t('asideInfo.category')}>
                    <ul className={css.tagList}>{Tag(categoryName)}</ul>
                </Section>
            ) : null}

            {supportedLanguages ? (
                <Section heading={t('asideInfo.languages')}>
                    <ul className={css.tagList}>
                        {supportedLanguages.map((l) => t(`contentfulLangs.${l}`)).map(Tag)}
                    </ul>
                </Section>
            ) : null}

            <Section heading={t('asideInfo.more')}>
                <ul className={css.moreList}>
                    {moreLinks
                        .filter((link) => link.href)
                        .map(({ href, icon, text }) => {
                            return (
                                <li key={href}>
                                    <Button
                                        className={css.infoLink}
                                        variant="tertiary"
                                        startIcon={icon}
                                        href={href}
                                        target="_blank"
                                        gap="large"
                                    >
                                        {text}
                                    </Button>
                                </li>
                            )
                        })}
                </ul>
            </Section>
        </aside>
    )
}

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
    return (
        <section>
            <Text className={css.asideHeading} tag="h5" size="body-2xs">
                {heading}
            </Text>
            {children}
        </section>
    )
}

function Tag(text: string) {
    return <MetaPill key={text}>{text}</MetaPill>
}
